import { useMemo, useState } from 'react';
import { DashboardPanel } from './DashboardLayout';
import { AnalyticsChart, ChartTypeToggle, GroupedComparisonChart } from './DashboardCharts';
import {
  ENTITY_ANALYTICS_CONFIG,
  buildEntityChartData,
  entityTypesForRole,
  isPercentageEntityMetric
} from '../utils/dashboardAnalytics';
import {
  applicationColumns,
  jobAnalyticsColumns,
  clientAnalyticsColumns,
  recruiterAnalyticsColumns,
  candidateAnalyticsColumns,
  mapApplications,
  mapJobAnalytics,
  mapClientAnalytics,
  mapRecruiterAnalytics,
  mapCandidateAnalytics
} from '../utils/dashboardColumns';
import { maskPersonName } from '../utils/maskCandidatePii';

function ExportLink({ label = 'Export all', onClick }) {
  return (
    <button
      type="button"
      className="text-[10px] font-medium text-[hsl(var(--ds-primary))] hover:underline"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function entityRowsForType(type, { jobStats, clientStats, recruiterStats, candidateStats }) {
  if (type === 'job') return jobStats || [];
  if (type === 'client') return clientStats || [];
  if (type === 'recruiter') return recruiterStats || [];
  if (type === 'candidate') return candidateStats || [];
  return [];
}

function entityExportConfig(type) {
  if (type === 'job') {
    return { columns: jobAnalyticsColumns, map: mapJobAnalytics, filename: 'job_analytics', title: 'Job-wise Analytics' };
  }
  if (type === 'client') {
    return {
      columns: clientAnalyticsColumns,
      map: mapClientAnalytics,
      filename: 'client_analytics',
      title: 'Client-wise Analytics'
    };
  }
  if (type === 'recruiter') {
    return {
      columns: recruiterAnalyticsColumns,
      map: mapRecruiterAnalytics,
      filename: 'recruiter_analytics',
      title: 'Recruiter Analytics'
    };
  }
  return {
    columns: candidateAnalyticsColumns,
    map: mapCandidateAnalytics,
    filename: 'candidate_analytics',
    title: 'Candidate Analytics'
  };
}

function entityDetailInsight(type, row) {
  if (type === 'job') {
    return {
      title: `Job: ${row.title}`,
      filename: `job_${row.id}`,
      columns: applicationColumns,
      rows: mapApplications(row.allApps),
      subtitle: `${row.joinedCount} joined · ${row.interviewToJoinedPct}% interview→joined · ${row.offerToJoinedPct}% offer→joined`
    };
  }
  if (type === 'client') {
    return {
      title: `Client: ${row.name}`,
      filename: `client_${row.id}`,
      columns: applicationColumns,
      rows: mapApplications(row.clientApps),
      subtitle: `${row.joinedCount} joined · ${row.jobsCompletedPct}% jobs completed`
    };
  }
  if (type === 'recruiter') {
    return {
      title: `Recruiter: ${row.name}`,
      filename: `recruiter_${row.id}`,
      columns: applicationColumns,
      rows: mapApplications(row.recruiterApps),
      subtitle: `${row.completedJobs}/${row.assignedJobs} jobs completed · ${row.interviewToJoinedPct}% interview→joined`
    };
  }
  return {
    title: `Candidate: ${row.name}`,
    filename: `candidate_${row.id}`,
    columns: applicationColumns,
    rows: mapApplications(row.allApps),
    subtitle: `${row.applicationCount} application(s) · best stage: ${row.bestStage}`
  };
}

export default function EntityAnalyticsExplorer({
  role,
  jobStats = [],
  clientStats = [],
  recruiterStats = [],
  candidateStats = [],
  onOpenInsight
}) {
  const availableTypes = entityTypesForRole(role);
  const [entityType, setEntityType] = useState(availableTypes[0] || 'job');
  const [metric, setMetric] = useState(ENTITY_ANALYTICS_CONFIG[entityType]?.metrics[0]?.id || 'joined');
  const [chartMode, setChartMode] = useState('combo');

  const config = ENTITY_ANALYTICS_CONFIG[entityType];
  const rows = entityRowsForType(entityType, { jobStats, clientStats, recruiterStats, candidateStats });

  const chartBundle = useMemo(() => {
    if (!config) return { chartData: {}, rowByLabel: {}, isPercentage: false };
    return buildEntityChartData(rows, metric, config.labelKey);
  }, [rows, metric, config]);

  const effectiveChartMode =
    metric === 'pipelineCompare' || isPercentageEntityMetric(metric) ? 'bar' : chartMode;

  const handleEntityTypeChange = nextType => {
    setEntityType(nextType);
    const firstMetric = ENTITY_ANALYTICS_CONFIG[nextType]?.metrics[0]?.id || 'joined';
    setMetric(firstMetric);
  };

  const openExportAll = () => {
    const exp = entityExportConfig(entityType);
    onOpenInsight({
      title: exp.title,
      filename: exp.filename,
      columns: exp.columns,
      rows: exp.map(rows, { maskForClient: role === 'client' && entityType === 'candidate' }),
      subtitle: `${rows.length} record(s) — full analytics export`,
      maskPii: role === 'client' && entityType === 'candidate',
      candidateProfileIds: role === 'client' && entityType === 'candidate'
    });
  };

  const openEntityDetail = row => {
    if (!row) return;
    const insight = entityDetailInsight(entityType, row);
    const maskCandidate = role === 'client' && entityType === 'candidate';
    const apps =
      entityType === 'job'
        ? row.allApps
        : entityType === 'client'
          ? row.clientApps
          : entityType === 'recruiter'
            ? row.recruiterApps
            : row.allApps;
    onOpenInsight({
      ...insight,
      title: maskCandidate ? `Candidate: ${maskPersonName(row.name)}` : insight.title,
      rows: mapApplications(apps, { maskForClient: maskCandidate }),
      maskPii: maskCandidate,
      candidateProfileIds: maskCandidate
    });
  };

  const metricLabel = config?.metrics.find(m => m.id === metric)?.label || metric;

  return (
    <DashboardPanel
      title="Entity Analytics"
      subtitle="Choose entity and metric — charts update instantly. Click any segment for details and Excel export."
      action={
        rows.length ? (
          <div className="flex items-center gap-2">
            <ChartTypeToggle mode={effectiveChartMode} onChange={setChartMode} />
            <ExportLink onClick={openExportAll} />
          </div>
        ) : null
      }
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-0.5">
          {availableTypes.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => handleEntityTypeChange(type)}
              className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                entityType === type
                  ? 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] shadow-sm border border-[hsl(var(--ds-primary)/0.25)]'
                  : 'text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
              }`}
            >
              {ENTITY_ANALYTICS_CONFIG[type]?.label || type}
            </button>
          ))}
        </div>

        <select
          value={metric}
          onChange={e => setMetric(e.target.value)}
          className="text-xs border border-[hsl(var(--ds-border))] rounded-lg px-2 py-1.5 bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] min-w-[10rem]"
          aria-label="Analytics metric"
        >
          {(config?.metrics || []).map(m => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>

        <span className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
          {rows.length} {config?.label?.toLowerCase() || 'entities'}
          {metric === 'pipelineCompare' || isPercentageEntityMetric(metric)
            ? ' · bar chart recommended for this metric'
            : ''}
        </span>
      </div>

      {!rows.length ? (
        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No {config?.label?.toLowerCase() || 'entity'} data in your scope.</p>
      ) : chartBundle.isComparison ? (
        <GroupedComparisonChart
          groups={chartBundle.groups}
          onGroupClick={group => openEntityDetail(group.row)}
          valueSuffix={isPercentageEntityMetric(metric) ? '%' : ''}
        />
      ) : (
        <AnalyticsChart
          mode={effectiveChartMode}
          data={chartBundle.chartData}
          onSegmentClick={label => openEntityDetail(chartBundle.rowByLabel[label])}
          labelFormat={key => (isPercentageEntityMetric(metric) ? `${key} %` : key)}
        />
      )}

      {rows.length > 0 && (
        <p className="text-[10px] text-[hsl(var(--ds-muted-fg))] mt-3 text-center">
          Showing top {Math.min(12, rows.length)} by {metricLabel} — use Export all for the complete list
        </p>
      )}
    </DashboardPanel>
  );
}
